/**
 * RBAC — frontend-only role-based access control.
 *
 * Single source of truth for *what each persona can see and do*. The UI
 * (sidebar, route guard, drawer action buttons) consults this matrix —
 * it never makes per-page decisions inline.
 *
 * In Phase 1 (mock data) this is purely a UX scaffold; Phase 2 will mirror
 * the same matrix on the backend and resolve permissions from the IdP claim.
 *
 * Verbs:
 *   view:<surface>        — can navigate to / see the surface
 *   submit:opportunity    — can create new opportunities
 *   approve:<stage>       — can issue an approve / reject / changes decision
 *                            at the given workflow stage (1..7)
 *   publish:policy        — can mark a policy active / draft
 *   manage:framework      — can upload evidence / close gaps
 *   verify:audit-chain    — can press the "Verify chain" CTA
 *   switch:tenant         — can switch between demo tenants / customer mode
 *
 * Two read-only personas (`auditor`, `execviewer`) are forced read-only via
 * the `isReadOnly()` helper — even verbs they would otherwise carry are
 * stripped, leaving them with `view:*` and `verify:audit-chain` only.
 */

// ----- Surface list (= nav section IDs in navConfig.js + standalone routes) ---
export const SURFACES = [
  "home",
  "identify",
  "deploy",
  "govern.agents",
  "govern.policies",
  "govern.oversight",
  "govern.incidents",
  "measure",
  "compliance",
  "vendors",
  "reports",
  "commercial",
  "settings.users",
  "settings.audit",
];

// ----- Verb → personas matrix ----------------------------------------------
// PERMISSIONS[verb] = Set of persona ids allowed to perform `verb`.
//
// Read in plain English:
//   - view:opportunities allowed for all operational personas
//   - approve:stage-4 allowed only for CAIO (final sign-off)
//   - publish:policy allowed for Legal + CRO + CAIO
//   - manage:framework allowed for Legal + CAIO
//
// "auditor" and "execviewer" appear here only on view-type verbs; their
// special "everything else is forbidden" status is enforced in `can()`.
const ALL_OPERATIONAL = ["caio", "cio", "cro", "ceo", "itsec", "legal", "dslead", "buhead"];

const M = (...ids) => new Set(ids);

const PERMISSIONS = {
  // ----- View permissions (who sees what in the sidebar / can land on the URL)
  "view:home":             M(...ALL_OPERATIONAL, "auditor", "execviewer"),
  "view:identify":         M("caio", "cio", "cro", "ceo", "dslead", "buhead", "auditor"),
  "view:deploy":           M("caio", "cio", "cro", "itsec", "legal", "dslead", "buhead", "auditor"),
  "view:govern.agents":    M("caio", "cio", "cro", "itsec", "dslead", "buhead", "auditor"),
  "view:govern.policies":  M("caio", "cro", "legal", "itsec", "auditor"),
  "view:govern.oversight": M("caio", "cro", "itsec", "dslead", "auditor"),
  "view:govern.incidents": M("caio", "cro", "itsec", "dslead", "auditor"),
  "view:measure":          M("caio", "cio", "ceo", "dslead", "execviewer"),
  "view:compliance":       M("caio", "cio", "cro", "ceo", "legal", "auditor", "execviewer"),
  "view:vendors":          M("caio", "cio", "itsec", "auditor"),
  "view:reports":          M("caio", "cio", "cro", "ceo", "legal", "auditor", "execviewer"),
  "view:commercial":       M("caio", "cio", "ceo", "execviewer"),
  "view:settings.users":   M("caio", "itsec"),
  "view:settings.audit":   M("caio", "cro", "itsec", "legal", "auditor"),

  // ----- Action permissions
  "submit:opportunity":    M("caio", "cio", "cro", "dslead", "buhead"),
  "approve:stage-1":       M("caio", "cio", "cro", "dslead", "buhead"),
  "approve:stage-2":       M("caio", "itsec"),
  "approve:stage-3":       M("caio", "cro", "legal"),
  "approve:stage-4":       M("caio"),
  "approve:stage-5":       M("caio", "dslead"),
  "approve:stage-6":       M("caio", "dslead"),

  "publish:policy":        M("caio", "cro", "legal"),
  "manage:framework":      M("caio", "legal"),
  "decide:hil":            M("caio", "cro", "itsec", "legal", "dslead", "buhead"),
  "manage:incident":       M("caio", "cro", "itsec", "dslead"),
  "verify:audit-chain":    M(...ALL_OPERATIONAL, "auditor"),
  "switch:tenant":         M(...ALL_OPERATIONAL, "auditor", "execviewer"),
};

// Read-only personas are forbidden every verb starting with these prefixes.
const READ_ONLY_PERSONAS = new Set(["auditor", "execviewer"]);
const MUTATING_VERB_PREFIXES = ["submit:", "approve:", "publish:", "manage:", "decide:"];

/**
 * The single permission check. Returns boolean.
 *
 *   can("caio", "approve:stage-4") === true
 *   can("auditor", "publish:policy") === false (read-only override)
 *   can(undefined, ...) === false
 */
export function can(personaId, verb) {
  if (!personaId || !verb) return false;
  if (
    READ_ONLY_PERSONAS.has(personaId) &&
    MUTATING_VERB_PREFIXES.some((p) => verb.startsWith(p))
  ) {
    return false;
  }
  const allowed = PERMISSIONS[verb];
  return allowed ? allowed.has(personaId) : false;
}

export function isReadOnly(personaId) {
  return READ_ONLY_PERSONAS.has(personaId);
}

/**
 * For sidebar rendering: returns the list of surface IDs a persona may see.
 */
export function visibleSurfaces(personaId) {
  return SURFACES.filter((s) => can(personaId, `view:${s}`));
}

/**
 * Tooltip / aria-label copy for a disabled mutating control.
 */
export function readOnlyReason(personaId) {
  if (personaId === "auditor") return "Read-only — Auditor persona cannot mutate governance state.";
  if (personaId === "execviewer") return "Read-only — Executive Viewer cannot perform actions.";
  return "You do not have permission to perform this action — contact your CAIO.";
}
